/**
 * Chuông thông báo của web quầy (docs/plans/thong-bao-chia-se-chon-tiem.md T1–T2).
 *
 * Việc của nó: khách tự đặt / tự huỷ / xin chờ qua Zalo thì quầy BIẾT NGAY, không phải tình cờ nhìn
 * vào màn Lịch. Ba lớp, từ rẻ tới đắt:
 *   1. Hỏi máy chủ mỗi 30 giây (và ngay khi tab hiện lại) → số đỏ trên chuông + "(2)" trên tiêu đề tab
 *      + một tiếng "ting" ngắn. Chạy trên MỌI máy, không cần cho phép gì.
 *   2. Tab đang ẩn và đã cho phép thông báo → hiện thông báo của hệ điều hành (qua service worker).
 *   3. "Báo về máy này" (Web Push) → máy kêu cả khi đã đóng app. Cần cho phép; iPhone phải cài app.
 *
 * Mốc "đã xem tới đâu" lưu TRÊN MÁY (localStorage, bọc try/catch): đó là tiện ích của từng máy, mất
 * thì chỉ là chấm đỏ hiện lại — không phải dữ liệu nghiệp vụ.
 */

import { formatVnDateShort, formatVnTime, parseIsoStrict, toVnDateKey } from '@sapa/shared'
import {
  Banknote,
  Bell,
  BellOff,
  BellRing,
  CalendarClock,
  CalendarPlus,
  CalendarX2,
  Hourglass,
  MessageSquareText,
  ReceiptText,
  Wallet,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type Api, type DangKyDay, type TinQuay, type ToiLaAi, tienVnd } from './api.ts'
import { tomTatTinMoi } from './chi-nhanh-dang-dung.ts'
import { useChiNhanh } from './chi-nhanh-dang-dung.tsx'
import { dangChayNhuUngDung, laIos } from './pwa.ts'
import { khiNao, TB } from './text/thong-bao.ts'
import { Rong, ThongBao, To } from './ui.tsx'

const CHU_KY_MS = 30_000

function docMoc(khoa: string): number {
  try {
    const v = window.localStorage.getItem(khoa)
    return v === null ? 0 : Number.parseInt(v, 10) || 0
  } catch {
    return 0
  }
}

function ghiMoc(khoa: string, ms: number): void {
  try {
    window.localStorage.setItem(khoa, String(ms))
  } catch {
    // Chế độ riêng tư / bị chặn: chấm đỏ sẽ hiện lại lần sau — chấp nhận được.
  }
}

/** Một tiếng "ting-ting" ngắn tự tổng hợp (không tải file âm thanh). Trình duyệt chặn thì thôi. */
let loa: AudioContext | null = null
function moLoa(): void {
  try {
    loa ??= new AudioContext()
    if (loa.state === 'suspended') void loa.resume()
  } catch {
    loa = null
  }
}
function keu(): void {
  if (loa === null || loa.state !== 'running') return
  const bd = loa.currentTime
  for (const [i, tan] of [880, 1320].entries()) {
    const o = loa.createOscillator()
    const g = loa.createGain()
    o.type = 'sine'
    o.frequency.value = tan
    g.gain.setValueAtTime(0.0001, bd + i * 0.16)
    g.gain.exponentialRampToValueAtTime(0.25, bd + i * 0.16 + 0.02)
    g.gain.exponentialRampToValueAtTime(0.0001, bd + i * 0.16 + 0.15)
    o.connect(g).connect(loa.destination)
    o.start(bd + i * 0.16)
    o.stop(bd + i * 0.16 + 0.16)
  }
}

/** "10:00 · Thứ Hai 05/10 · Gội đầu · Chị Lan" — không có số điện thoại (máy chủ cũng không gửi). */
function dongPhu(t: TinQuay): string {
  // Một tin có dữ liệu lạ chỉ được mất dòng phụ, không được làm cả ngăn thông báo sập.
  try {
    return dongPhuThat(t)
  } catch {
    return ''
  }
}

function dongPhuThat(t: TinQuay): string {
  // 0065: tin cho chủ tiệm — số hoá đơn · tiền · người làm (không gắn lịch).
  if (t.loai === 'hoa_don_huy') {
    return [t.soHoaDon ?? '', t.tienVnd == null ? '' : tienVnd(t.tienVnd), t.nguoi ?? '']
      .filter((x) => x !== '')
      .join(' · ')
  }
  if (t.loai === 'ket_lech') {
    const tien = t.tienVnd ?? '0'
    const lech = tien.startsWith('-')
      ? TB.ketThieu(tienVnd(tien.slice(1)))
      : TB.ketDu(tienVnd(tien))
    return [lech, t.nguoi ?? ''].filter((x) => x !== '').join(' · ')
  }
  const phan: string[] = []
  if (t.batDau !== null)
    phan.push(`${formatVnTime(parseIsoStrict(t.batDau))} ${formatVnDateShort(ngayCua(t.batDau))}`)
  else if (t.ngayCho !== null) phan.push(formatVnDateShort(t.ngayCho))
  if (t.dichVu !== '') phan.push(t.dichVu)
  phan.push(t.khach ?? TB.chuaTen)
  return phan.join(' · ')
}

/** Ngày theo giờ Việt Nam (`YYYY-MM-DD`) của một thời điểm ISO có offset. */
function ngayCua(iso: string): string {
  return toVnDateKey(parseIsoStrict(iso))
}

const ICON = {
  lich_moi: CalendarPlus,
  khach_huy: CalendarX2,
  xin_cho: Hourglass,
  cho_duyet: CalendarPlus,
  chua_xac_nhan: CalendarClock,
  khach_doi_gio: CalendarClock,
  khach_nhan: MessageSquareText,
  cho_coc: Banknote,
  het_coc: CalendarX2,
  het_duyet: CalendarX2,
  hoa_don_huy: ReceiptText,
  ket_lech: Wallet,
} as const

function base64UrlSangByte(s: string): Uint8Array<ArrayBuffer> {
  const dem = '='.repeat((4 - (s.length % 4)) % 4)
  const b = window.atob((s + dem).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(b.length))
  for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i)
  return out
}

type TrangThaiDay = 'khong-ho-tro' | 'ios-can-cai' | 'bi-chan' | 'tat' | 'bat' | 'dang-lam'

/** Tính trạng thái "báo về máy" của máy này. */
async function docTrangThaiDay(api: Api, khoa: string | null): Promise<TrangThaiDay> {
  if (khoa === null) return 'khong-ho-tro'
  if (laIos() && !dangChayNhuUngDung()) return 'ios-can-cai'
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window))
    return 'khong-ho-tro'
  if (Notification.permission === 'denied') return 'bi-chan'
  const dk = await navigator.serviceWorker.getRegistration()
  const sub = await dk?.pushManager.getSubscription()
  if (sub === null || sub === undefined) return 'tat'
  const r = await api.trangThaiDay(sub.endpoint)
  return r.dangNhan ? 'bat' : 'tat'
}

/**
 * Hỏi máy chủ theo chu kỳ, trả số tin chưa xem và danh sách. Tách khỏi phần vẽ để cả thanh bên
 * (tablet) lẫn nút nổi (điện thoại) dùng chung một nguồn.
 */
export function useChuong(api: Api, toi: ToiLaAi) {
  const khoa = `sapa-tb-da-xem-${toi.tiem}-${toi.toi.id}`
  const [tin, setTin] = useState<TinQuay[] | null>(null)
  const [loi, setLoi] = useState(false)
  const [daXem, setDaXem] = useState(() => docMoc(khoa))
  const daBiet = useRef<Set<string> | null>(null)

  const tai = useCallback(() => {
    void api
      .thongBao()
      .then((r) => {
        setLoi(false)
        setTin(r.tin)
        const cu = daBiet.current
        const moi = cu === null ? [] : r.tin.filter((t) => !cu.has(t.id))
        daBiet.current = new Set(r.tin.map((t) => t.id))
        if (moi.length > 0) {
          keu()
          // Tab đang ẩn + đã cho phép → hiện thông báo của hệ điều hành. Máy đã bật Web Push thì
          // service worker tự hiện rồi — tránh kêu hai lần bằng cờ `tag` trùng nhau.
          if (
            document.hidden &&
            'Notification' in window &&
            Notification.permission === 'granted'
          ) {
            const dau = moi[0]
            if (dau !== undefined) {
              void navigator.serviceWorker?.getRegistration().then((dk) =>
                dk?.showNotification(TB.loai[dau.loai], {
                  body: dongPhu(dau).replace(/ · [^·]*$/, ''),
                  tag: `sapa-${dau.loai}`,
                  icon: '/icon-192.png',
                }),
              )
            }
          }
        }
      })
      .catch(() => setLoi(true))
  }, [api])

  useEffect(() => {
    tai()
    const h = window.setInterval(() => {
      if (!document.hidden) tai()
    }, CHU_KY_MS)
    const khiHien = () => {
      if (!document.hidden) tai()
    }
    document.addEventListener('visibilitychange', khiHien)
    // Trình duyệt chỉ cho phát âm thanh sau một lần chạm: mở "loa" ở lần chạm đầu tiên.
    const cham = () => moLoa()
    window.addEventListener('pointerdown', cham, { once: true })
    return () => {
      window.clearInterval(h)
      document.removeEventListener('visibilitychange', khiHien)
      window.removeEventListener('pointerdown', cham)
    }
  }, [tai])

  const soMoi = (tin ?? []).filter((t) => Date.parse(t.luc) > daXem).length

  // "(2) Lịch hẹn" trên tab: thấy được cả khi đang ở tab khác của trình duyệt.
  useEffect(() => {
    const goc = document.title.replace(/^\(\d+\) /, '')
    document.title = soMoi > 0 ? `(${soMoi}) ${goc}` : goc
  }, [soMoi])

  function danhDauDaXem() {
    // Mốc theo giờ MÁY CHỦ của tin mới nhất, không theo đồng hồ tablet (chạy nhanh thì nuốt tin mới).
    const moiNhat = Math.max(daXem, ...(tin ?? []).map((t) => Date.parse(t.luc)))
    setDaXem(moiNhat)
    ghiMoc(khoa, moiNhat)
  }

  return { tin, loi, soMoi, daXem, danhDauDaXem, taiLai: tai }
}

/** Nút chuông (dùng ở thanh bên tablet và nút nổi điện thoại). */
export function NutChuong({
  soMoi,
  onMo,
  kieu,
}: {
  soMoi: number
  onMo: () => void
  kieu: 'thanh-ben' | 'noi'
}) {
  const nhan = soMoi > 0 ? `${TB.nut} (${soMoi})` : TB.nut
  return (
    <button
      type="button"
      className={kieu === 'thanh-ben' ? 'muc-dh nut-chuong' : 'nut-chuong-noi'}
      aria-label={nhan}
      onClick={onMo}
    >
      <span className="chuong-icon">
        {soMoi > 0 ? (
          <BellRing size={24} strokeWidth={1.9} />
        ) : (
          <Bell size={24} strokeWidth={1.9} />
        )}
        {soMoi > 0 && <span className="cham-so">{soMoi > 9 ? '9+' : soMoi}</span>}
      </span>
      {kieu === 'thanh-ben' && TB.nut}
    </button>
  )
}

/** Nơi bấm một tin dẫn tới. `branchId` = chi nhánh CỦA TIN (sự cố 31/10: bấm tin chi nhánh khác thì thấy lịch trống). */
export interface DichBamTin {
  /** Tin về một lịch: ngày của lịch, chi nhánh của tin, và id lịch để mở luôn tờ chi tiết. */
  onMoLich: (ngay: string, branchId: string, lichId: string | null) => void
  onMoCho: (branchId: string) => void
  /** 0065: tin của chủ tiệm → Sổ tiền → "Bị huỷ" (ngày huỷ, màn cả tiệm — không kèm chi nhánh) / "Két" (kèm chi nhánh). */
  onMoSoTien: (the: 'huy' | 'ket', ngay: string, branchId?: string) => void
}

/** Bấm một tin trong ngăn → đi đúng chỗ, mang theo chi nhánh của tin (hàm thuần, test không cần bấm thật). */
export function bamTin(t: TinQuay, dich: DichBamTin): void {
  const ngay = t.batDau === null ? null : ngayCua(t.batDau)
  if (t.loai === 'xin_cho') dich.onMoCho(t.branchId)
  else if (t.loai === 'hoa_don_huy') dich.onMoSoTien('huy', ngayCua(t.luc))
  else if (t.loai === 'ket_lech') dich.onMoSoTien('ket', ngayCua(t.luc), t.branchId)
  else if (ngay !== null) dich.onMoLich(ngay, t.branchId, t.lichId)
}

/** Ngăn thông báo: danh sách tin + công tắc "Báo về máy này". */
export function NganThongBao({
  api,
  chuong,
  onDong,
  onMoLich,
  onMoCho,
  onMoSoTien,
}: {
  api: Api
  chuong: ReturnType<typeof useChuong>
  onDong: () => void
} & DichBamTin) {
  const { tin, loi, daXem } = chuong
  const cn = useChiNhanh()
  /** Tiệm một chi nhánh: `[]` và không nhãn → ngăn chuông y hệt trước. */
  const tomTat = tin === null ? [] : tomTatTinMoi(tin, daXem, cn)
  const [khoa, setKhoa] = useState<string | null | undefined>(undefined)
  const [day, setDay] = useState<TrangThaiDay | null>(null)
  const [bao, setBao] = useState<{ loai: 'loi' | 'thanh-cong'; chu: string } | null>(null)
  const bayGio = Date.now()

  /** Đóng ngăn = đã xem: chấm "mới" vẫn hiện trong lúc đang mở để biết tin nào vừa tới. */
  function dong() {
    chuong.danhDauDaXem()
    onDong()
  }

  useEffect(() => {
    void api
      .khoaDay()
      .then((r) => {
        setKhoa(r.khoa)
        return docTrangThaiDay(api, r.khoa)
      })
      .then(setDay)
      .catch(() => setDay('khong-ho-tro'))
  }, [api])

  async function batDay() {
    if (khoa === null || khoa === undefined) return
    setDay('dang-lam')
    try {
      // Xin quyền NGAY trong lần bấm (iOS bắt buộc).
      const quyen = await Notification.requestPermission()
      if (quyen !== 'granted') {
        setDay(quyen === 'denied' ? 'bi-chan' : 'tat')
        return
      }
      const dk = await navigator.serviceWorker.ready
      const sub =
        (await dk.pushManager.getSubscription()) ??
        (await dk.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64UrlSangByte(khoa),
        }))
      const j = sub.toJSON()
      const dkDay: DangKyDay = {
        endpoint: sub.endpoint,
        keys: { p256dh: j.keys?.p256dh ?? '', auth: j.keys?.auth ?? '' },
      }
      await api.dangKyDay(dkDay)
      setDay('bat')
      setBao({ loai: 'thanh-cong', chu: TB.dayXong })
    } catch {
      setDay('tat')
      setBao({ loai: 'loi', chu: TB.dayLoi })
    }
  }

  async function tatDay() {
    setDay('dang-lam')
    try {
      const dk = await navigator.serviceWorker.getRegistration()
      const sub = await dk?.pushManager.getSubscription()
      if (sub !== null && sub !== undefined) {
        await api.huyDay(sub.endpoint)
        await sub.unsubscribe()
      }
      setDay('tat')
      setBao({ loai: 'thanh-cong', chu: TB.dayDaTat })
    } catch {
      setDay('bat')
      setBao({ loai: 'loi', chu: TB.dayLoi })
    }
  }

  return (
    <To nhan={TB.tieuDe} onDong={dong}>
      <header className="to-dau">
        <div>
          <h2>{TB.tieuDe}</h2>
          <p className="chu-phu">{TB.moTa}</p>
        </div>
        <button type="button" className="nut nut-nhat nut-icon" aria-label={TB.dong} onClick={dong}>
          <X size={22} />
        </button>
      </header>
      <div className="to-than ngan-thong-bao">
        {loi && tin === null && <ThongBao loai="loi">{TB.loiTai}</ThongBao>}
        {tin !== null && tin.length === 0 && (
          <Rong icon={<Bell size={28} />} tieuDe={TB.trong} moTa={TB.trongPhu} />
        )}
        {tomTat.length > 0 && (
          <div className="tom-tat-cn">
            {tomTat.map((x) => (
              <p key={x.id} className="dong-tom-tat-cn">
                {TB.moiOChiNhanh(x.n, x.ten)}
              </p>
            ))}
          </div>
        )}
        {tin !== null && tin.length > 0 && (
          <ul className="ds-tin">
            {tin.map((t) => {
              /*
               * Loại tin lạ (tab mở từ trước lúc máy chủ thêm loại tin mới) → biểu tượng chuông + nhãn chung,
               * KHÔNG được để `Icon` là undefined: React ném lỗi lúc vẽ và cả màn hình trắng (phản ánh 30/09,
               * bấm tin "hoá đơn bị huỷ" trên bản web mở từ trước lần cập nhật).
               */
              const Icon = (ICON as Record<string, typeof Bell>)[t.loai] ?? Bell
              const nhanLoai = (TB.loai as Record<string, string>)[t.loai] ?? TB.tieuDe
              const moi = Date.parse(t.luc) > daXem
              // Chỉ khi tiệm ≥ 2 chi nhánh VÀ chi nhánh của tin chọn được: tiệm một chi nhánh không vẽ gì thêm.
              const tenCn = cn.nhieu ? (cn.ds?.find((c) => c.id === t.branchId)?.name ?? '') : ''
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className={`dong-tin loai-${t.loai} ${moi ? 'moi' : ''}`}
                    onClick={() => {
                      // Bấm vào một tin = đã xem cả ngăn (ngăn đóng lại, không đi qua nút Đóng).
                      chuong.danhDauDaXem()
                      bamTin(t, { onMoLich, onMoCho, onMoSoTien })
                    }}
                  >
                    <span className="icon-tin" aria-hidden="true">
                      <Icon size={20} />
                    </span>
                    <span className="chu-tin">
                      {tenCn !== '' && (
                        <span className="nhan-cn" role="img" aria-label={TB.nhanCnAria(tenCn)}>
                          {tenCn}
                        </span>
                      )}
                      <b>
                        {nhanLoai}
                        {t.soLoiNhan > 1 && ` · ${TB.soLoi(t.soLoiNhan)}`}
                      </b>
                      <span>{dongPhu(t)}</span>
                      {/* Chỉ trong ngăn đã đăng nhập — tin đẩy / thông báo máy KHÔNG in lời nhắn. */}
                      {t.noiDung !== null && (
                        <span className="noi-dung-tin">{TB.trichLoi(t.noiDung)}</span>
                      )}
                      {t.lyDo != null && t.lyDo !== '' && (
                        <span className="noi-dung-tin">{TB.trichLoi(t.lyDo)}</span>
                      )}
                    </span>
                    <span className="luc-tin">
                      {moi && <span className="cham-moi" aria-hidden="true" />}
                      {khiNao(t.luc, bayGio)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        <section className="khoi-day" aria-label={TB.dayTieuDe}>
          <div className="khoi-day-dau">
            {day === 'bat' ? <BellRing size={20} /> : <BellOff size={20} />}
            <div>
              <b>{TB.dayTieuDe}</b>
              <p className="chu-phu">{day === 'bat' ? TB.dayDangBat : TB.dayMoTa}</p>
            </div>
          </div>
          {day === 'tat' && (
            <button type="button" className="nut nut-vien nut-rong" onClick={() => void batDay()}>
              {TB.dayBat}
            </button>
          )}
          {day === 'bat' && (
            <button type="button" className="nut nut-nhat nut-rong" onClick={() => void tatDay()}>
              {TB.dayTat}
            </button>
          )}
          {day === 'dang-lam' && (
            <button type="button" className="nut nut-vien nut-rong" disabled>
              {TB.dangMo}
            </button>
          )}
          {day === 'ios-can-cai' && <p className="chu-phu">{TB.dayIos}</p>}
          {day === 'bi-chan' && <p className="chu-phu">{TB.dayBiChan}</p>}
          {day === 'khong-ho-tro' && khoa !== undefined && (
            <p className="chu-phu">{TB.dayKhongHoTro}</p>
          )}
          {bao !== null && (
            <ThongBao loai={bao.loai} onDong={() => setBao(null)}>
              {bao.chu}
            </ThongBao>
          )}
        </section>
      </div>
    </To>
  )
}
